import { useState } from 'react';
import { Target, Settings2, ListChecks, LineChart } from 'lucide-react';
import AddendumModal from '../AddendumModal';
import NoteAddendumsList from '../NoteAddendumsList';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatClinicalDateTime } from '../../i18n/localeFormat';

const ICON_COLORS = {
  diagnosis: 'text-rose-600',
  goals: 'text-blue-600',
  interventions: 'text-emerald-600',
  evaluation: 'text-purple-600',
};

function SectionBlock({ icon: Icon, title, children, tone }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${ICON_COLORS[tone]}`} strokeWidth={2} />
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );
}

export default function CarePlanCard({
  carePlan,
  onAddAddendum,
  onResolve,
  onExportPdf,
  resolving = false,
  savingAddendum = false,
}) {
  const { t, language } = useLanguage();
  const [addendumOpen, setAddendumOpen] = useState(false);
  const plan = carePlan.care_plan || {};
  const isResolved = carePlan.status === 'resolved';

  return (
    <article className="card-surface space-y-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
              isResolved ? 'bg-gray-200 text-gray-700' : 'bg-rose-100 text-rose-700'
            }`}
          >
            {isResolved ? t('resolved') : t('active')}
          </span>
          <p className="mt-2 text-sm text-gray-500">
            {formatClinicalDateTime(carePlan.datetime, language)}
          </p>
        </div>
      </div>

      <SectionBlock icon={Target} title={t('nursingDiagnosis')} tone="diagnosis">
        <p className="whitespace-pre-wrap text-sm">{plan.nursing_diagnosis || '—'}</p>
      </SectionBlock>

      <SectionBlock icon={ListChecks} title={t('carePlanGoals')} tone="goals">
        <p className="whitespace-pre-wrap text-sm">{plan.goals || '—'}</p>
      </SectionBlock>

      <SectionBlock icon={Settings2} title={t('carePlanInterventions')} tone="interventions">
        {(plan.interventions || []).length ? (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {plan.interventions.map((item, index) => (
              <li key={`${carePlan.id}-intervention-${index}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm">—</p>
        )}
      </SectionBlock>

      <SectionBlock icon={LineChart} title={t('carePlanEvaluation')} tone="evaluation">
        <p className="whitespace-pre-wrap text-sm">{plan.evaluation || '—'}</p>
      </SectionBlock>

      <NoteAddendumsList note={{ addendums: carePlan.addenda || [] }} />

      <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setAddendumOpen(true)}
          className="rounded-full border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
        >
          + {t('addAddendum')}
        </button>
        {!isResolved && (
          <button
            type="button"
            onClick={() => onResolve?.(carePlan.id)}
            disabled={resolving}
            className="rounded-full border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60"
          >
            {t('markAsResolved')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onExportPdf?.(carePlan)}
          className="rounded-full bg-primary px-3 py-2 text-sm font-semibold text-white"
        >
          {t('exportPdf')}
        </button>
      </div>

      <AddendumModal
        open={addendumOpen}
        onClose={() => setAddendumOpen(false)}
        saving={savingAddendum}
        onSave={(payload) => {
          onAddAddendum?.(carePlan.id, payload);
          setAddendumOpen(false);
        }}
      />
    </article>
  );
}
